<template>
  <b-row class="w-100 mx-auto">
    <b-col class="p-0">
      <base-card-indicator variant="info" class="mb-u8 card__indicator">
        <div class="d-flex align-items-center justify-content-between">
          <h5 ref="card__indicator-title" class="font-weight-bold card__indicator-title mb-0">
            Feedback for Earner & Issuer
          </h5>
          <div v-if="!readonly">
            <b-badge
              v-if="giveFeedback || allowAssess"
              class="badge__feedback-status"
              :variant="allFeedbackIsDone ? 'neutral-success' : 'neutral-danger'"
              pill
              :class="allFeedbackIsDone ? 'text-success' : 'text-danger'"
            >
              {{ allFeedbackIsDone ? 'Completed' : 'Not Completed' }}
            </b-badge>
          </div>
        </div>
      </base-card-indicator>

      <b-card class="card-box card__submit-submission w-100 mx-auto">
        <b-form id="form-feedback-recommend-evidence" @submit.prevent="onSaveChanges">
          <div class="mb-u10">
            <div
              v-if="!giveFeedback && !readonly"
              class="d-flex align-items-center justify-content-between mb-4"
            >
              <b-button
                variant="outline-primary"
                class="button__edit-feedback-recommend-evidence"
                :disabled="submitting"
                @click="onGiveFeedback"
              >
                <b-spinner v-if="isLoadingPermission" small />
                <span v-else>
                  {{ formFeedbackForEarner ? 'Edit' : 'Give feedback' }}
                </span>
              </b-button>
            </div>

            <!-- Feedback for Earner -->
            <div class="d-flex align-items-center justify-content-between mb-3">
              <div>
                <p class="font-weight-bold mb-1 title__feedback-for-earner">
                  Feedback for Earner (Optional)
                </p>
                <p class="text-black-50 m-0 description__feedback-for-earner">
                  Provide your overall feedback on the learner's performance in this field.
                </p>
              </div>
            </div>

            <div class="response__content">
              <base-text-editor
                v-if="readonly && formFeedbackForEarner"
                v-model="formFeedbackForEarner"
                class="readonly text__feedback_for_earner"
                :show-toolbar="false"
                :disabled="true"
                :drop-image="false"
              >
              </base-text-editor>

              <base-text-editor
                v-else
                id="feedback_for_earner"
                ref="input__feedback-for-earner"
                v-model="formFeedbackForEarner"
                name="feedback_for_earner"
                class="input__feedback-for-earner"
                :show-toolbar="!readonly"
                :disabled="submitting || readonly || !giveFeedback"
                :placeholder="!readonly ? 'Enter your comment here...' : ''"
              >
              </base-text-editor>
            </div>
          </div>

          <!-- Assessment Result -->
          <div class="mb-u10">
            <div class="mb-3">
              <p class="font-weight-bold title__assessment-result">Assessment Result</p>
            </div>
            <b-form-group
              :disabled="submitting || readonly || !giveFeedback"
              class="radio__group-result"
            >
              <b-form-radio
                v-model="formEvidencePass"
                name="final-result"
                :value="true"
                class="mb-3"
                disable-event-track
                @change.native="onRadioChange"
              >
                <h5 class="text__result-award">Award</h5>
              </b-form-radio>

              <b-form-radio
                v-model="formEvidencePass"
                name="final-result"
                :value="false"
                disable-event-track
                @change.native="onRadioChange"
              >
                <h5 class="text__result-deny">Deny</h5>
              </b-form-radio>

              <!-- Assessor's recommendation -->
              <div v-if="formEvidencePass" class="container__assessor-recommendation mt-4">
                <div class="text__title mb-3">
                  <p class="font-weight-bold mb-1 title__assessor-recommendation">
                    Assessor's recommendation
                  </p>
                  <p class="text-black-50 description__assessor-recommendation">
                    Write a brief recommendation explaining why the learner is awarded this
                    competency. Highlight any notable achievements or qualities.
                  </p>
                </div>

                <b-alert
                  v-if="giveFeedback && !issuerId"
                  show
                  class="alert__assessor-recommendation"
                >
                  <b-img :src="iconAlert" class="icon__alert" />
                  <span>
                    Keep in mind that this recommendation will be visible to third parties reviewing
                    the digital badge to validate the learner's competency.
                  </span>
                </b-alert>

                <div class="response__content">
                  <base-text-editor
                    v-if="readonly && formAssessorRecommendation"
                    v-model="formAssessorRecommendation"
                    class="readonly text__assessor-recommendation"
                    :show-toolbar="false"
                    :disabled="true"
                    :drop-image="false"
                    :placeholder="!readonly ? 'Enter your comment here...' : ''"
                  >
                  </base-text-editor>

                  <base-text-editor
                    v-else
                    id="assessor-recommendation"
                    ref="input__assessor-recommendation"
                    v-model="formAssessorRecommendation"
                    name="assessor-recommendation"
                    class="input__assessor-recommendation"
                    :class="{
                      text_editor__required: onChanges && !formAssessorRecommendation
                    }"
                    :show-toolbar="!readonly"
                    :disabled="submitting || !giveFeedback"
                    :placeholder="!readonly ? 'Enter your comment here...' : ''"
                  >
                  </base-text-editor>
                </div>
              </div>
            </b-form-group>
          </div>

          <!-- Comments for Issuer Only -->
          <div class="mb-u10">
            <div class="mb-3">
              <p class="font-weight-bold mb-1 title__comments-for-issuer-only">
                Comments for Issuer Only
              </p>
              <p class="text-black-50 description__comments-for-issuer-only">
                Provide a brief comment for the issuer (4LifelongLearning), explaining the reasoning
                or confirmation behind the assessment result.
              </p>
            </div>

            <b-alert v-if="giveFeedback && !issuerId" show class="alert__comments-for-issuer">
              <b-img :src="iconAlert" class="icon__alert" />
              <span>
                This text will only be visible to the issuer and is not shared with the learner or
                third parties.
              </span>
            </b-alert>

            <div class="response__content">
              <base-text-editor
                v-if="(readonly && formCommentForIssuer) || issuerId"
                v-model="formCommentForIssuer"
                class="readonly text__comments-for-issuer"
                :show-toolbar="false"
                :disabled="true"
                :drop-image="false"
              >
              </base-text-editor>

              <div v-else>
                <base-text-editor
                  id="comment_for_issuer"
                  ref="input__comments-for-issuer"
                  v-model="formCommentForIssuer"
                  name="comment_for_issuer"
                  class="input__comments-for-issuer"
                  :class="{
                    text_editor__required: onChanges && !formCommentForIssuer
                  }"
                  :show-toolbar="!readonly"
                  :disabled="submitting || readonly || !giveFeedback"
                  :placeholder="!readonly ? 'Enter your comment here...' : ''"
                ></base-text-editor>
              </div>
            </div>
          </div>

          <!-- MC Design Suggestions (Optional) -->
          <div class="mb-u10">
            <div class="mb-3">
              <p class="font-weight-bold mb-1 title__mc-design-suggestions">
                MC Design Suggestions (Optional)
              </p>
              <p class="text-black-50 description__mc-design-suggestions">
                Provide feedback or additional notes for other assessors regarding the assessment
                process, or to share suggestions with the micro-credential designer for future
                design improvements.
              </p>
            </div>

            <div class="response__content">
              <base-text-editor
                v-if="readonly && formDesignSuggestion"
                v-model="formDesignSuggestion"
                class="readonly text__mc-design-suggestions"
                :show-toolbar="false"
                :disabled="true"
                :drop-image="false"
              >
              </base-text-editor>

              <base-text-editor
                v-else
                id="mc-design-suggestions"
                ref="input__mc-design-suggestions"
                v-model="formDesignSuggestion"
                name="mc-design-suggestions"
                class="input__mc-design-suggestions"
                :show-toolbar="!readonly"
                :disabled="submitting || readonly || !giveFeedback"
                :placeholder="!readonly ? 'Enter your comment here...' : ''"
              ></base-text-editor>
            </div>
          </div>

          <div v-if="giveFeedback" class="mt-3">
            <b-button
              type="submit"
              variant="success"
              class="button__save-changes"
              :disabled="changing"
            >
              <b-spinner v-if="changing" small /><span v-else>Save changes</span>
            </b-button>
            <b-button variant="link" :disabled="changing" @click="onClickCancel">Cancel</b-button>
          </div>
        </b-form>
      </b-card>
    </b-col>
  </b-row>
</template>

<script>
import BaseTextEditor from '@/components/base-components/BaseTextEditor';
import LearningUnit from '@/models/LearningUnit';
import EvidenceSubmission from '@/models/EvidenceSubmission';
import { isBoolean } from 'lodash';
import { amplitudeMixin } from '@/mixins/amplitudeMixin';
import { notificationMixin } from '@/mixins/notificationMixin';
import BaseCardIndicator from '@/components/base-components/BaseCardIndicator';
import iconAlert from '@/assets/icon-alert/icon-circle-exclamation-white.svg';

export default {
  components: {
    BaseTextEditor,
    BaseCardIndicator
  },
  mixins: [amplitudeMixin, notificationMixin],

  props: {
    postEvaluationAssessments: {
      type: Array,
      required: true
    },

    learningUnit: {
      type: LearningUnit,
      required: true
    },

    evidenceSubmission: {
      type: EvidenceSubmission,
      default: () => null
    },

    allowAssess: {
      type: Boolean,
      default: false
    },

    readonly: {
      type: Boolean,
      default: false
    },

    assessorId: {
      type: [String, Number],
      default: null
    },

    issuerId: {
      type: [String, Number],
      default: null
    }
  },

  data() {
    return {
      submitting: false,
      giveFeedback: false,
      changing: false,
      onChanges: false,
      formFeedbackForEarner: this.evidenceSubmission
        ? this.evidenceSubmission.feedback_for_earner
        : '',
      formCommentForIssuer: this.evidenceSubmission
        ? this.evidenceSubmission.comment_for_issuer
        : '',
      formAssessorRecommendation: this.evidenceSubmission
        ? this.evidenceSubmission.assessor_recommendation
        : '',
      formEvidencePass: this.evidenceSubmission ? this.evidenceSubmission.pass : null,
      formDesignSuggestion: this.evidenceSubmission
        ? this.evidenceSubmission.design_suggestion
        : '',
      isLoadingPermission: false,
      iconAlert
    };
  },

  computed: {
    allFeedbackIsDone() {
      const { is_feedback_editing, comment_for_issuer, pass } = this.evidenceSubmission;

      const hasAllFeedback =
        !is_feedback_editing &&
        (this.issuerId || (comment_for_issuer && comment_for_issuer.length > 0));

      const hasAssessmentResult = isBoolean(pass);

      return hasAllFeedback && hasAssessmentResult;
    }
  },

  watch: {
    evidenceSubmission: {
      handler: 'onSelectedEvidenceSubmissionChanged'
    }
  },
  methods: {
    onSelectedEvidenceSubmissionChanged() {
      const {
        feedback_for_earner,
        comment_for_issuer,
        assessor_recommendation,
        pass,
        design_suggestion
      } = this.evidenceSubmission;

      this.formFeedbackForEarner = feedback_for_earner;

      this.formCommentForIssuer = comment_for_issuer;

      this.formAssessorRecommendation = assessor_recommendation;

      this.formEvidencePass = pass;

      this.formDesignSuggestion = design_suggestion;
    },

    async isHavePermission() {
      const canFeedback = await EvidenceSubmission.api().canFeedback(this.evidenceSubmission.id);
      if (!canFeedback) {
        this.$toast.error('You don’t have permission to evaluate this submission.');
        return this.$router.push({
          name: 'design-planning/info',
          params: { learningUnitId: this.evidenceSubmission.learning_unit_id },
          query: { tab: 'assess' }
        });
      }
    },

    async saveChangesByAssessor() {
      try {
        this.changing = true;

        await EvidenceSubmission.api().updateByAssessor(this.evidenceSubmission.id, {
          pass: this.formEvidencePass,
          feedback_for_earner: this.formFeedbackForEarner,
          comment_for_issuer: this.formCommentForIssuer,
          assessor_recommendation: this.formAssessorRecommendation,
          design_suggestion: this.formDesignSuggestion
        });

        this.$toast.success('Your evaluation has been saved successfully');
        EvidenceSubmission.update({
          where: this.evidenceSubmission.id,
          data: { is_feedback_editing: false }
        });
        this.giveFeedback = false;
      } catch (error) {
        console.log(error);
        this.onClickCancel();
        if (error?.response && error.response.status === 403) {
          this.$toast.error('You don’t have permission to evaluate this submission.');
          return this.$router.push({
            name: 'design-planning/info',
            params: { learningUnitId: this.evidenceSubmission.learning_unit_id },
            query: { tab: 'assess' }
          });
        }
        this.$toast.error('Cannot saved your evaluation, please try again');
      } finally {
        this.changing = false;
      }
    },

    async saveChangesByIssuer() {
      try {
        this.changing = true;

        await EvidenceSubmission.api().update(this.evidenceSubmission.id, {
          pass: this.formEvidencePass,
          feedback_for_earner: this.formFeedbackForEarner,
          assessor_recommendation: this.formAssessorRecommendation,
          design_suggestion: this.formDesignSuggestion
        });

        this.$toast.success('Your evaluation has been saved successfully');

        EvidenceSubmission.update({
          where: this.evidenceSubmission.id,
          data: { is_feedback_editing: false }
        });
        this.giveFeedback = false;
      } catch (error) {
        console.log(error);
        this.onClickCancel();
        this.$toast.error('Cannot saved your evaluation, please try again');
      } finally {
        this.changing = false;
      }
    },

    async onSaveChanges() {
      this.onChanges = true;

      const missingCommentForIssuer = !this.issuerId && !this.formCommentForIssuer;
      const invalidEvidencePass = !isBoolean(this.formEvidencePass);
      const missingAssessorRecommendation =
        this.formEvidencePass && !this.formAssessorRecommendation;

      if (missingCommentForIssuer || invalidEvidencePass || missingAssessorRecommendation) {
        if (invalidEvidencePass) this.notiError('Please select the assessment result.');
        if (missingAssessorRecommendation)
          this.notiError('Please insert the assessor’s recommendation.');
        if (missingCommentForIssuer) this.notiError('Please insert the comments for issuer.');
        return;
      }

      if (this.assessorId && !this.evidenceSubmission.assessor_reviewed_at) {
        this.saveChangesByAssessor();
      }

      if (this.issuerId && !this.evidenceSubmission.issuer_reviewed_at) {
        this.saveChangesByIssuer();
      }
    },

    onClickCancel() {
      this.formFeedbackForEarner = this.evidenceSubmission
        ? this.evidenceSubmission.feedback_for_earner
        : null;

      this.formCommentForIssuer = this.evidenceSubmission
        ? this.evidenceSubmission.comment_for_issuer
        : null;

      this.formAssessorRecommendation = this.evidenceSubmission
        ? this.evidenceSubmission.assessor_recommendation
        : '';
      this.formEvidencePass = this.evidenceSubmission ? this.evidenceSubmission.pass : null;

      this.formDesignSuggestion = this.evidenceSubmission
        ? this.evidenceSubmission.design_suggestion
        : '';
      this.onChanges = false;

      EvidenceSubmission.update({
        where: this.evidenceSubmission.id,
        data: { is_feedback_editing: false }
      });
      this.giveFeedback = false;
    },

    onRadioChange(event) {
      const { value } = event.target;

      this.$_amplitudeMixin_onChange(event.target, {
        Criteria: value ? 'Award' : 'Deny',
        Value: value
      });
    },

    async onGiveFeedback() {
      EvidenceSubmission.update({
        where: this.evidenceSubmission.id,
        data: { is_feedback_editing: true }
      });
      this.giveFeedback = true;
    }
  }
};
</script>

<style lang="scss">
.card__submit-submission {
  max-width: 910px;
}

.readonly {
  .ql-container.ql-snow {
    border-radius: 0.65rem !important;

    .ql-editor {
      border-radius: 0.65rem !important;
    }
  }
}

.text_editor__required {
  .ql-container.ql-snow {
    background: #ffffff !important;
    border: 1px solid #f83245 !important;
    border-radius: 0 0 4.64px 4.64px !important;
  }

  .ql-toolbar.ql-snow {
    background: #ffffff !important;
    border: 1px solid #f83245 !important;
    border-radius: 4.64px 4.64px 0 0 !important;
    border-bottom: none !important;
  }
}
.text-sucess {
  color: #1bc943 !important;
}
.text-danger {
  color: #f83245 !important;
}

.text__feedback_for_earner,
.text__comments-for-issuer {
  display: flex;
  flex-direction: column;

  img,
  p {
    max-width: -moz-available; /* WebKit-based browsers will ignore this. */
    max-width: -webkit-fill-available; /* Mozilla-based browsers will ignore this. */
  }
}

.alert__comments-for-issuer,
.alert__assessor-recommendation {
  display: flex;
  align-items: flex-start;
  gap: 16px;

  .icon__alert {
    color: #ffffff;
    background-color: #0c6b7e;
    padding: 6px;
    border-radius: 8px;

    img {
      width: 18px;
    }
  }
}
</style>
